// frontend-react/src/hooks/useKeeperSelection.js
import { useState, useEffect, useCallback } from 'react';
import keeperService from '../services/keeperService';

/**
 * Custom hook for managing keeper selection state and API integration
 * 
 * @param {string} leagueId - League ID
 * @param {string} teamId - Team ID
 * @returns {object} Keeper selection state and actions
 */
export const useKeeperSelection = (leagueId, teamId, leagueStatus = null) => {
  // Data state
  const [eligibleKeepers, setEligibleKeepers] = useState([]);
  const [selectedKeepers, setSelectedKeepers] = useState([]);
  const [status, setStatus] = useState(null);
  const [semanticSeasons, setSemanticSeasons] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Local selection state with position assignments
  // Format: { player_id: { roster_position, extension_type, extension_salary } }
  const [keeperAssignments, setKeeperAssignments] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // Track original extension state for submitted keepers (to detect changes)
  const [originalExtensions, setOriginalExtensions] = useState({});

  /**
   * Load all keeper data from API
   */
  const loadData = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setLoading(true);
      setError(null);

      // Load all data in parallel
      const [eligibleResponse, selectionsResponse, statusResponse] = await Promise.all([
        keeperService.getEligibleKeepers(leagueId, teamId),
        keeperService.getKeeperSelections(leagueId, teamId),
        keeperService.getKeeperStatus(leagueId, teamId)
      ]);

      console.log('Keeper data loaded:', {
        eligible: eligibleResponse,
        selections: selectionsResponse,
        status: statusResponse
      });

      // Set eligible keepers
      if (eligibleResponse.success && eligibleResponse.eligible_keepers) {
        setEligibleKeepers(eligibleResponse.eligible_keepers);
      }

      // Capture semantic season fields from API response
      if (eligibleResponse.keeper_season) {
        setSemanticSeasons({
          keeper_season: eligibleResponse.keeper_season,
          historical_season: eligibleResponse.historical_season,
          projected_season: eligibleResponse.projected_season,
          league_status: eligibleResponse.league_status,
        });
      }

      // Set current selections
      if (selectionsResponse.success && selectionsResponse.keeper_selections) {
        setSelectedKeepers(selectionsResponse.keeper_selections);
        
        // Initialize keeper assignments from saved/final keepers
        const savedAssignments = {};
        const origExtensions = {};
        selectionsResponse.keeper_selections
          .filter(k => k.keeper?.status === 'saved' || k.keeper?.status === 'final')
          .forEach(k => {
            savedAssignments[k.player_id] = {
              roster_position: k.league_context?.roster?.position,
              extension_type: k.keeper?.extension_type || null,
              extension_salary: k.keeper?.extension_salary || null
            };
            // Track original extensions for submitted keepers
            if (k.keeper?.status === 'final') {
              origExtensions[k.player_id] = {
                extension_type: k.keeper?.extension_type || null,
                extension_salary: k.keeper?.extension_salary || null
              };
            }
          });
        setKeeperAssignments(savedAssignments);
        setOriginalExtensions(origExtensions);
      }

      // Set status
      if (statusResponse.success && statusResponse.status) {
        setStatus(statusResponse.status);
      }

      setIsDirty(false);
    } catch (err) {
      console.error('Error loading keeper data:', err);
      setError(err.message || 'Failed to load keeper data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, teamId]);

  /**
   * Assign keeper to a roster position
   * @param {number} playerId - MLB player ID
   * @param {string} rosterPosition - Roster slot (e.g., 'SS_0', 'OF_1')
   */
  const assignKeeperPosition = useCallback((playerId, rosterPosition) => {
    console.log('🎯 assignKeeperPosition:', playerId, '->', rosterPosition);
    console.log('Current assignments:', keeperAssignments);
    console.log('Max keepers:', status?.max_keepers);
    
    setKeeperAssignments(prev => {
      // Check if at max keepers (and not replacing existing)
      if (!prev[playerId] && Object.keys(prev).length >= status?.max_keepers) {
        console.warn('Cannot add more keepers - at maximum');
        return prev;
      }
      
      const newAssignments = { ...prev };
      newAssignments[playerId] = {
        ...(prev[playerId] || {}),
        roster_position: rosterPosition
      };
      setIsDirty(true);
      return newAssignments;
    });
  }, [keeperAssignments, status]);

  /**
   * Remove keeper (unassign from roster)
   * @param {number} playerId - MLB player ID
   */
  const removeKeeper = useCallback((playerId) => {
    console.log('🗑️ removeKeeper:', playerId);
    
    setKeeperAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[playerId];
      setIsDirty(true);
      return newAssignments;
    });
  }, []);

  /**
   * Select or clear an extension for a keeper
   * @param {number} playerId - MLB player ID
   * @param {object|null} extensionOption - Extension option ({contract_type, cost, years, cost_increase}) or null to clear
   */
  const selectExtension = useCallback((playerId, extensionOption) => {
    setKeeperAssignments(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        extension_type: extensionOption?.contract_type || null,
        extension_salary: extensionOption?.cost || null
      }
    }));
    setIsDirty(true);
  }, []);

  /**
   * Save draft selections with position assignments to API
   */
  const saveDraft = useCallback(async () => {
    if (!leagueId || !teamId || !isDirty) return;

    try {
      setSaving(true);
      setError(null);

      // Convert assignments to API format
      const keeperAssignmentsList = Object.entries(keeperAssignments).map(([playerId, assignment]) => ({
        player_id: parseInt(playerId),
        roster_position: assignment.roster_position,
        extension_type: assignment.extension_type || null,
        extension_salary: assignment.extension_salary || null
      }));

      console.log('💾 Saving keeper assignments:', keeperAssignmentsList);

      const response = await keeperService.saveKeeperDraft(leagueId, teamId, keeperAssignmentsList);

      console.log('Save draft response:', response);

      if (!response.success) {
        throw new Error(response.message || 'Failed to save draft');
      }

      // If during draft, update extensions on submitted (final) keepers via dedicated endpoint
      if (leagueStatus === 'draft') {
        const extensionUpdates = [];
        for (const [playerId, assignment] of Object.entries(keeperAssignments)) {
          const orig = originalExtensions[playerId];
          if (!orig) continue; // Not a submitted keeper
          if (orig.extension_type !== (assignment.extension_type || null) ||
              orig.extension_salary !== (assignment.extension_salary || null)) {
            extensionUpdates.push(
              keeperService.updateKeeperExtension(leagueId, teamId, {
                player_id: parseInt(playerId),
                extension_type: assignment.extension_type || null,
                extension_salary: assignment.extension_salary || null
              })
            );
          }
        }
        if (extensionUpdates.length > 0) {
          console.log(`🔄 Updating ${extensionUpdates.length} extension(s) on submitted keepers`);
          await Promise.all(extensionUpdates);
        }
      }

      // Reload data to get updated status
      await loadData();
      setIsDirty(false);
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save draft');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [leagueId, teamId, keeperAssignments, isDirty, loadData, leagueStatus, originalExtensions]);

  /**
   * Submit keeper selections (locks them in)
   */
  const submitKeepers = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setSubmitting(true);
      setError(null);

      // Save any unsaved changes before submitting
      if (isDirty) {
        await saveDraft();
      }

      const response = await keeperService.submitKeepers(leagueId, teamId);

      console.log('Submit response:', response);

      if (response.success) {
        // Reload data to get updated status
        await loadData();
      } else {
        throw new Error(response.message || 'Failed to submit keepers');
      }
    } catch (err) {
      console.error('Error submitting keepers:', err);
      setError(err.message || 'Failed to submit keepers');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [leagueId, teamId, loadData, isDirty, saveDraft]);

  /**
   * Unsubmit keeper selections (unlock them)
   */
  const unsubmitKeepers = useCallback(async () => {
    if (!leagueId || !teamId) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await keeperService.unsubmitKeepers(leagueId, teamId);

      console.log('Unsubmit response:', response);

      if (response.success) {
        // Reload data to get updated status
        await loadData();
      } else {
        throw new Error(response.message || 'Failed to unsubmit keepers');
      }
    } catch (err) {
      console.error('Error unsubmitting keepers:', err);
      setError(err.message || 'Failed to unsubmit keepers');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [leagueId, teamId, loadData]);

  /**
   * Computed properties
   */
  const canSave = isDirty && !saving && !submitting && !status?.is_submitted;
  const canSubmit = status && !status.is_submitted && status.counts.saved > 0;
  const canUnsubmit = status && status.is_submitted;
  const selectedPlayerIds = Object.keys(keeperAssignments).map(id => parseInt(id));
  const selectedCount = Object.keys(keeperAssignments).length;

  // Load data on mount and when leagueId/teamId change
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Data
    eligibleKeepers,
    selectedKeepers,
    status,
    semanticSeasons,
    
    // Loading states
    loading,
    saving,
    submitting,
    error,
    
    // Actions
    assignKeeperPosition,
    removeKeeper,
    selectExtension,
    saveDraft,
    submitKeepers,
    unsubmitKeepers,
    refreshData: loadData,
    
    // Computed
    isDirty,
    canSave,
    canSubmit,
    canUnsubmit,
    selectedPlayerIds,
    selectedCount,
    keeperAssignments
  };
};

export default useKeeperSelection;
